/* Les contacts d'une usine ou d'un client, par domaine.
 *
 * Un contact porte un ou plusieurs domaines : c'est ce qui décide à qui part
 * un bon de commande, une facture ou une réclamation. Une adresse de service
 * — qualite@ — est marquée générique : on l'écrit sans nom de personne. */

import { Fragment, useState } from "react";
import { supabase } from "@/lib/supabase";
import { Empty, Section, Tag } from "@/ui/primitives";
import { Icon } from "@/ui/Icon";
import { useToast } from "@/ui/Toast";
import type { Contact, DomaineContact } from "@/types/metier";

export const DOMAINES: { cle: DomaineContact; nom: string; court: string }[] = [
  { cle: "customer", nom: "Commercial", court: "Com." },
  { cle: "finance", nom: "Finance", court: "Fin." },
  { cle: "quality", nom: "Qualité", court: "Qual." },
  { cle: "packaging", nom: "Packaging", court: "Pack." },
  { cle: "logistics", nom: "Logistique", court: "Log." },
];

export function nomDomaine(d: DomaineContact): string {
  return DOMAINES.find((x) => x.cle === d)?.nom ?? d;
}

/** Qui reçoit quoi : le premier contact de chaque domaine fait foi. */
export function Responsables({ liste }: { liste: Contact[] }) {
  return (
    <dl className="kv">
      {DOMAINES.map((d) => {
        const qui = liste.filter((c) => c.domaines?.includes(d.cle));
        return (
          <Fragment key={d.cle}>
            <dt>{d.nom}</dt>
            <dd>
              {qui.length ? (
                qui.map((c) => c.nom).join(", ")
              ) : (
                <span className="locked">personne de désigné</span>
              )}
            </dd>
          </Fragment>
        );
      })}
    </dl>
  );
}

interface Props {
  table: "contacts_usine" | "contacts_client";
  /** La colonne qui rattache le contact : usine_id ou client_id. */
  colonne: "usine_id" | "client_id";
  parent: string;
  liste: Contact[];
  modifiable: boolean;
  recharger: () => void;
}

export function EditeurContacts({ table, colonne, parent, liste, modifiable, recharger }: Props) {
  const toast = useToast();
  const [occupe, setOccupe] = useState(false);

  async function agir(quoi: () => Promise<void>, message: string) {
    if (occupe) return;
    setOccupe(true);
    try {
      await quoi();
      recharger();
      toast(message);
    } catch (e) {
      toast(e instanceof Error ? e.message : "L'enregistrement a échoué.");
    } finally {
      setOccupe(false);
    }
  }

  const ecrire = async (id: string, patch: Partial<Contact>) => {
    const { error } = await supabase.from(table).update(patch).eq("id", id);
    if (error) throw new Error(error.message);
  };

  const ajouter = () =>
    void agir(async () => {
      const { error } = await supabase
        .from(table)
        .insert({ [colonne]: parent, nom: "Nouveau contact", domaines: [] });
      if (error) throw new Error(error.message);
    }, "Contact ajouté.");

  const supprimer = (c: Contact) =>
    void agir(async () => {
      const { error } = await supabase.from(table).delete().eq("id", c.id);
      if (error) throw new Error(error.message);
    }, `${c.nom} retiré des contacts.`);

  const basculer = (c: Contact, d: DomaineContact) =>
    void agir(async () => {
      const actuels = c.domaines ?? [];
      const suivants = actuels.includes(d)
        ? actuels.filter((x) => x !== d)
        : [...actuels, d];
      await ecrire(c.id, { domaines: suivants });
    }, "Domaines mis à jour.");

  return (
    <Section
      titre="Contacts"
      note={`${liste.length} ${liste.length > 1 ? "personnes" : "personne"}`}
      droite={
        modifiable ? (
          <button type="button" className="btn btn-sm" disabled={occupe} onClick={ajouter}>
            <Icon n="plus" cls="" />
            Ajouter
          </button>
        ) : null
      }
    >
      <div className="card card-pad">
        {liste.length === 0 ? (
          <Empty titre="Aucun contact" detail="Les documents partiront à l'adresse générale." />
        ) : (
          <div className="contacts">
            {liste.map((c) => (
              <div className="contact" key={c.id}>
                <div className="champ-duo">
                  <div className="champ">
                    <label htmlFor={`c-nom-${c.id}`}>Nom</label>
                    <input
                      id={`c-nom-${c.id}`}
                      type="text"
                      defaultValue={c.nom}
                      disabled={!modifiable || occupe}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (!v || v === c.nom) return;
                        void agir(() => ecrire(c.id, { nom: v }), "Nom mis à jour.");
                      }}
                    />
                  </div>
                  <div className="champ">
                    <label htmlFor={`c-mail-${c.id}`}>Adresse</label>
                    <input
                      id={`c-mail-${c.id}`}
                      type="email"
                      defaultValue={c.email ?? ""}
                      disabled={!modifiable || occupe}
                      onBlur={(e) => {
                        const v = e.target.value.trim() || null;
                        if (v === (c.email ?? null)) return;
                        void agir(() => ecrire(c.id, { email: v }), "Adresse mise à jour.");
                      }}
                    />
                  </div>
                </div>
                <div className="champ-duo">
                  <div className="champ">
                    <label htmlFor={`c-tel-${c.id}`}>Téléphone</label>
                    <input
                      id={`c-tel-${c.id}`}
                      type="tel"
                      defaultValue={c.tel ?? ""}
                      disabled={!modifiable || occupe}
                      onBlur={(e) => {
                        const v = e.target.value.trim() || null;
                        if (v === (c.tel ?? null)) return;
                        void agir(() => ecrire(c.id, { tel: v }), "Téléphone mis à jour.");
                      }}
                    />
                  </div>
                  <div className="champ">
                    <label>Domaines</label>
                    <div className="seg-pills">
                      {DOMAINES.map((d) => {
                        const on = c.domaines?.includes(d.cle) ?? false;
                        return modifiable ? (
                          <button
                            key={d.cle}
                            type="button"
                            className="pill"
                            aria-pressed={on}
                            disabled={occupe}
                            onClick={() => basculer(c, d.cle)}
                          >
                            {d.court}
                          </button>
                        ) : on ? (
                          <Tag key={d.cle}>{d.court}</Tag>
                        ) : null;
                      })}
                    </div>
                  </div>
                </div>
                {modifiable ? (
                  <div className="fiche-actions">
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost"
                      disabled={occupe}
                      onClick={() => supprimer(c)}
                    >
                      Retirer ce contact
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}
